import React from 'react';

// Deterministic monogram palette — mirrors /app/backend/routes/branding.py SPECIALTY_PALETTE.
// Kept in sync to ensure the doctor's monogram color is identical across dashboard + public website.
const SPECIALTY_PALETTE = {
  cardiology: '#DC2626', dermatology: '#DB2777', ent: '#7C3AED', gastroenterology: '#EA580C',
  physician: '#0284C7',                 // MD Medicine
  general_medicine: '#0284C7',          // legacy alias → physician
  general_surgery: '#0891B2', gynecology: '#BE185D', neurology: '#6D28D9',
  neurosurgery: '#4338CA', oncology: '#0F766E', ophthalmology: '#0369A1', orthopedics: '#1D4ED8',
  pediatrics: '#059669', psychiatry: '#7E22CE', pulmonology: '#0D9488', urology: '#B45309',
  dentistry: '#0E7490', endocrinology: '#B91C1C', nephrology: '#1E40AF', rheumatology: '#A21CAF',
  plastic_surgery: '#C026D3', allergy_immunology: '#15803D', radiology: '#525252', anesthesiology: '#475569',
  internal_medicine: '#0284C7',         // legacy alias → physician
  physiotherapy: '#16A34A', diabetology: '#D97706',
  infectious_disease: '#CA8A04', vascular_surgery: '#B91C1C', pain_management: '#9333EA',
};

export function computeMonogram(name = '', specializations = []) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  let initials = 'DR';
  if (parts.length === 1 && parts[0]) {
    initials = parts[0].slice(0, 2).toUpperCase();
  } else if (parts.length >= 2) {
    let first = parts[0];
    // Skip honorifics
    if (/^(dr|mr|ms|mrs|prof|doctor)\.?$/i.test(first) && parts.length >= 3) {
      initials = (parts[1][0] + parts[parts.length - 1][0]).toUpperCase();
    } else if (/^(dr|mr|ms|mrs|prof|doctor)\.?$/i.test(first) && parts.length === 2) {
      initials = parts[1].slice(0, 2).toUpperCase();
    } else {
      initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
  }
  let bg = '#0284C7';
  for (const s of specializations || []) {
    if (SPECIALTY_PALETTE[s]) { bg = SPECIALTY_PALETTE[s]; break; }
  }
  return { initials, bg_color: bg };
}

/**
 * MonogramAvatar — deterministic SVG circle with doctor initials.
 * Zero user-input rendering; safe by construction.
 */
export default function MonogramAvatar({ initials = 'DR', bgColor = '#0284C7', size = 56, className = '' }) {
  // Sanitize: allow only A-Z, 0-9, max 3 chars
  const safe = String(initials || 'DR').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3) || 'DR';
  // Sanitize hex color (fallback to default if odd)
  const safeColor = /^#[0-9A-Fa-f]{6}$/.test(bgColor) ? bgColor : '#0284C7';
  const fontSize = Math.round(size * (safe.length === 3 ? 0.36 : 0.42));

  return (
    <div
      role="img"
      aria-label={`${safe} monogram`}
      data-testid="monogram-avatar"
      className={`inline-flex items-center justify-center rounded-full flex-shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: safeColor,
        color: '#ffffff',
        fontFamily: 'Outfit, sans-serif',
        fontWeight: 600,
        fontSize,
        letterSpacing: safe.length === 3 ? '-0.02em' : '0',
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {safe}
    </div>
  );
}
